import { type StoreApi, type UseBoundStore, create } from "zustand"

import "../graphql/glPostingAccountOptions.query"

import { GL_POSTING_ACCOUNT_PAGE_SIZE } from "../constants"

import {
  GetGlPostingAccountOptionsDocument,
  type GetGlPostingAccountOptionsQuery,
  type GlAccount,
  GlAccountSortField,
  ReportingAccountType,
  SortingOrder,
} from "@/graphql/codegen/graphql"
import { type SearchFilters, type SearchStore, createSearchStore } from "@/stores/createSearchStore"

export interface GlPostingAccountFilters extends SearchFilters {
  query?: string
}

export type GlPostingAccountStore = UseBoundStore<StoreApi<SearchStore<GlAccount, GlPostingAccountFilters>>>

const deduplicateAccounts = (newAccounts: GlAccount[], existing: GlAccount[]): GlAccount[] => {
  const seen = new Set(existing.map((account) => account.glAccountId))
  return [...existing, ...newAccounts.filter((account) => account.glAccountId && !seen.has(account.glAccountId))]
}

// Factory, not a singleton — each transaction line's Account dropdown needs its own
// independent search/pagination state. A single shared store would leak one row's search
// query into every other row's option list (AK-6492).
export const createGlPostingAccountStore = (): GlPostingAccountStore =>
  create(
    createSearchStore<GlAccount, GlPostingAccountFilters, GetGlPostingAccountOptionsQuery>({
      documentNode: GetGlPostingAccountOptionsDocument,
      deduplicateData: deduplicateAccounts,
      initialState: { limit: GL_POSTING_ACCOUNT_PAGE_SIZE },
      getQueryInput: (filters: GlPostingAccountFilters, offset: number, limit: number) => ({
        filter: {
          reportingAccountType: ReportingAccountType.Posting,
          isActive: true,
          query: filters.query,
        },
        sort: { field: GlAccountSortField.Title, order: SortingOrder.Asc },
        offset,
        limit,
      }),
      getQueryVariables: (queryInput) => ({ input: queryInput }),
      extractDataFromResponse: (data) => ({
        __typename: data.glAccount.list.__typename,
        data:
          data.glAccount.list.__typename === "GlAccountListSuccess"
            ? data.glAccount.list.glAccounts.filter((account): account is GlAccount => account !== null)
            : [],
        error: data.glAccount.list.__typename === "GlAccountFailure" ? data.glAccount.list.error : undefined,
      }),
    })
  )
